// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type FC } from 'react';
import { Button, CircularProgress, Avatar } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { useTranslation } from 'react-i18next';

const getAdvisorDisplayName = (adv: any) => {
  if (adv.name && adv.name !== adv.email) return adv.name;
  return adv.email?.split('@')[0] || '';
};

interface ShareStepEmailFormProps {
  email: string;
  setEmail: (email: string) => void;
  onShare: (targetEmail?: string) => void;
  isSharing: boolean;
  advisors: any[];
  isLoadingAdvisors: boolean;
}

export const ShareStepEmailForm: FC<ShareStepEmailFormProps> = ({
  email,
  setEmail,
  onShare,
  isSharing,
  advisors,
  isLoadingAdvisors,
}) => {
  const { t } = useTranslation();
  return (
    <div className="px-6 py-0 pt-4">
      <label className="text-md mb-2 block font-medium text-border-dark">
        {t('aggregator.advisor-email-label', { ns: 'aggregator' })}
      </label>
      <div className="flex gap-2">
        <div className="relative flex flex-1 items-center rounded-xl border border-gray-200 bg-page transition-colors">
          <div className="absolute left-4 text-gray-400">
            <AppIcon icon="Mail" size="xl" />
          </div>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="h-[35px] w-full bg-transparent py-0 pl-12 pr-4 outline-none"
            placeholder={t('common.email-address', { ns: 'common' })}
          />
        </div>
        <Button
          variant="contained"
          color="secondary"
          disabled={!email || isSharing}
          onClick={() => onShare()}
          className="!h-[35px] !min-w-[40px] !rounded-xl"
        >
          {isSharing ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <AppIcon icon="ArrowRight" size="xl" color="light" />
          )}
        </Button>
      </div>
      <div className="mt-8 bg-page">
        <p className="text-md mb-4 !font-medium text-border-dark">
          {t('aggregator.suggested-advisors', { ns: 'aggregator' })}
        </p>
        {isLoadingAdvisors ? (
          <div className="flex justify-center py-4">
            <CircularProgress size={24} />
          </div>
        ) : (
          <div className="max-h-[300px] space-y-3 overflow-y-auto bg-page pr-1">
            {advisors?.map((adv: any) => {
              const displayName = getAdvisorDisplayName(adv);
              return (
                <div
                  key={adv.email}
                  onClick={() => setEmail(adv.email)}
                  className="flex cursor-pointer items-center justify-between rounded-xl border border-transparent bg-page p-4 transition-all hover:bg-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="!bg-secondary-light !h-10 !w-10 !text-sm !font-bold">
                      {displayName.charAt(0).toUpperCase()}
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-md font-medium text-title-alt">
                        {displayName}
                      </span>
                      <span className="text-sm text-primary-medium !opacity-100">
                        {adv.email}
                      </span>
                    </div>
                  </div>
                  <AppIcon icon="ArrowRight" size="sm" className="text-gray-300" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
